import { useState, useEffect } from 'react'
import { moderationApi, type MyReport } from '../../api/moderation'
import { useUiStore } from '../../stores/uiStore'

interface Props {
  onBack: () => void
}

const STATUS_LABEL: Record<string, { label: string; color: string }> = {
  pending:   { label: 'Pending',   color: 'text-yellow-400 bg-yellow-400/10' },
  resolved:  { label: 'Resolved',  color: 'text-green-400 bg-green-400/10'  },
  dismissed: { label: 'Dismissed', color: 'text-txt-meta bg-bg-hover'       },
}

function ReportCard({ report }: { report: MyReport }) {
  const s = STATUS_LABEL[report.status] ?? STATUS_LABEL.pending
  return (
    <div className="bg-bg-input rounded-2xl p-4 space-y-1.5">
      <div className="flex items-center justify-between gap-2">
        <p className="text-txt-primary text-sm font-medium capitalize">
          {report.reason.replace(/_/g, ' ')}
        </p>
        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${s.color}`}>
          {s.label}
        </span>
      </div>
      <p className="text-txt-meta text-xs capitalize">
        Target: {report.target_type}
        {report.target_id && <span className="ml-1 font-mono">#{report.target_id.slice(-6)}</span>}
      </p>
      {report.description && (
        <p className="text-txt-secondary text-xs line-clamp-2">{report.description}</p>
      )}
      {report.action_taken && (
        <p className="text-green-400 text-xs">Action taken: {report.action_taken}</p>
      )}
      <p className="text-txt-meta text-xs">
        {new Date(report.created_at).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
      </p>
    </div>
  )
}

export function MyReportsPanel({ onBack }: Props) {
  const [reports, setReports] = useState<MyReport[]>([])
  const [loading, setLoading] = useState(true)
  const { addToast } = useUiStore()

  useEffect(() => {
    moderationApi.listMyReports()
      .then((r) => setReports(r.reports ?? []))
      .catch(() => addToast('error', 'Не удалось загрузить жалобы'))
      .finally(() => setLoading(false))
  }, [addToast])

  return (
    <div className="flex flex-col h-full bg-bg-primary">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-divider shrink-0">
        <button onClick={onBack} className="text-txt-secondary hover:text-txt-primary transition-colors">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M12 5l-7 7 7 7"/>
          </svg>
        </button>
        <h2 className="text-txt-primary font-semibold text-base flex-1">My Reports</h2>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {loading && (
          <div className="flex justify-center py-8">
            <div className="w-5 h-5 border-2 border-accent/30 border-t-accent rounded-full animate-spin" />
          </div>
        )}

        {!loading && reports.length === 0 && (
          <div className="text-center py-10">
            <p className="text-3xl mb-2">🛡️</p>
            <p className="text-txt-secondary text-sm">No reports submitted yet</p>
          </div>
        )}

        {reports.map((r) => <ReportCard key={r.id} report={r} />)}
      </div>
    </div>
  )
}
